<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed, onBeforeMount, getCurrentInstance, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { IonApp } from "@ionic/vue";
import { PushNotifications } from "@capacitor/push-notifications";
import { NativeMarket } from "@capacitor-community/native-market";
import * as Sentry from "@sentry/capacitor";
import { SplashScreen } from "@capacitor/splash-screen";
import { Network } from "@capacitor/network";
import { App, type URLOpenListenerEvent } from "@capacitor/app";
import { AppTrackingTransparency } from "capacitor-plugin-app-tracking-transparency";
import { useHead } from "@unhead/vue";

import { type Component } from "vue";

import initSentry from "@/Plugins/Sentry";
import AppRouter from "./Router";

import { APP_ROUTES } from "@/Generated/Routes/paths";
import { config } from "@/Common/envConfig";

import TcModal from "@/Components/UI/TcModal";
import TcSnackbar from "@/Components/UI/TcSnackbar.vue";
import Privacy from "@/Components/Extend/TcPrivacy.vue";
import EventCapacitorManager from "@/Components/Extend/TcEventCapacitorManager.vue";
import RouterWrapper from "@/Router/RouterWrapper.vue";
import VideoCallVonageWrapper from "@/Modules/VideoCall/Components/WrapperVonage.vue";
import VideoCallRatingModal from "@/Modules/VideoCall/Components/RatingModal.vue";
import VideoCallConfig from "@/Modules/VideoCall/Components/VideoCallConfig.vue";

import useAppStore from "@/Modules/App/Store";
import useChatStore from "@/Modules/Chat/Store";
import useVideoCallStore from "@/Modules/VideoCall/Store";
import useAuthStore from "@/Modules/Auth/Store";
import useUserStore from "@/Modules/User/Store";
import { addTrackersAndCookiesBanner } from "@/Modules/App/TrackersAndCookies";
import { addGoogleGTM } from "@/Modules/App/TrackersAndCookies/GoogleGTM";

import { hasValue, isObject } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";
import { isAndroidApp, isApp, isIosApp, platformType, platforms } from "@/Utils/Ionic";
import { danger, log } from "@/Utils/Debug";
import { getTokenExpirationSeconds, isTokenWillExpireSoon } from "@/Utils/JWT";
import { waitFor } from "@/Utils/Promises";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const appStore = useAppStore();
const authStore = useAuthStore();
const userStore = useUserStore();
const chatStore = useChatStore();
const videoCallStore = useVideoCallStore();

const showPreview = ref<boolean>(false);
const trackersInjected = ref<boolean>(false);
const PreviewViewComp = ref<Component | null>(null);
let tokenRefreshTimeout: ReturnType<typeof setTimeout> | undefined;

const refreshTokenIfNeeded = async () => {
  const token = authStore.userToken;

  if (isValidString(token) && isTokenWillExpireSoon(token, config.jwtExpirationMinutes)) {
    await authStore.refreshToken();
  }
};

watch(
  () => authStore.userToken,
  (token) => {
    clearTimeout(tokenRefreshTimeout);

    if (!isValidString(token)) {
      return;
    }

    const expiration = getTokenExpirationSeconds(token);

    if (expiration === undefined) {
      return;
    }

    const refreshAt = expiration * 1000 - config.jwtExpirationMinutes * 60 * 1000;
    const delay = Math.max(refreshAt - Date.now(), 0);

    tokenRefreshTimeout = setTimeout(() => {
      void refreshTokenIfNeeded();
    }, delay);
  },
  { immediate: true }
);

const refreshTokenOnVisibilityChange = () => {
  if (document.visibilityState === "visible") {
    void refreshTokenIfNeeded();
  }
};

const updateViewport = () => {
  const { height, width } = appStore.getAppDisplay();

  if (height !== appStore.viewportHeight || width !== appStore.viewportWidth) {
    appStore.updateViewport(width, height);
  }
};

const updateStatusOnline = () => {
  appStore.updateStatusOnline(true);
};

const snackbarParams = computed(() => {
  if (hasValue(appStore.snackbar)) {
    return appStore.snackbar;
  } else {
    return { message: "" };
  }
});

const showVideoCallConfig = computed(() => videoCallStore.showConfig);

const hasVideoCallInProgress = computed(() => {
  return hasValue(userStore.user) && isValidString(videoCallStore.token);
});

const needRating = computed(() => {
  return (
    videoCallStore.isRatingRequired &&
    !hasValue(videoCallStore.nextCallOptions) &&
    isValidString(videoCallStore.roomId) &&
    !isValidString(videoCallStore.token)
  );
});

const updateStatusOffline = () => appStore.updateStatusOnline(false);

const handleUpdateApp = () => {
  if (isAndroidApp()) {
    NativeMarket.openStoreListing({ appId: "com.terapiachat.android" });
  } else if (isIosApp()) {
    NativeMarket.openStoreListing({ appId: "id1223472664" });
  }
};

const handleCloseUpdateApp = () => {
  if (!appStore.isForceUpdate) {
    appStore.needUpdate = false;
  }
};

const handleFinishPreview = () => {
  router.push({ name: APP_ROUTES.onboarding.root.name });

  waitFor(0.2).then(() => {
    showPreview.value = false;
  });
};

const handleGoToLogin = () => {
  router.push({ name: APP_ROUTES.login.root.name });

  showPreview.value = false;
};

const _configTrackers = async () => {
  log("ADDING TRACKERS");

  trackersInjected.value = true;
  addGoogleGTM();
  try {
    initSentry(getCurrentInstance()!.appContext.app, AppRouter.getInstance());

    const scope = Sentry.getCurrentScope();

    scope.clear();

    scope.setTag("app_version", config.appVersion!);

    if (isIosApp()) {
      scope.setTag("ios_version", config.appVersion!);
    } else if (isAndroidApp()) {
      scope.setTag("android_version", config.appVersion!);
    } else {
      scope.setTag("web_version", config.appVersion!);
    }

    scope.setTag("device_type", platformType());
    scope.setExtra("platforms", platforms().join(","));

    if (isValidString(config.sentryEnv)) {
      scope.setTag("environment", config.sentryEnv);
    }
  } catch (error) {
    log("SENTRY ERROR", error);
  }
};

const addTrackers = async () => {
  if (config.trackersDisabled) return;
  if (trackersInjected.value) return;
  if (!config.isEnabledTracking) return;

  if (
    !isApp() ||
    isAndroidApp() ||
    (await AppTrackingTransparency.getStatus().then(({ status }) => status === "authorized"))
  ) {
    _configTrackers();
  } else if (isIosApp()) {
    const response = await AppTrackingTransparency.requestPermission();

    if (response.status === "authorized") {
      log("TRACKING AUTHORIZED");
      _configTrackers();
    }
  }
};

const handleCompleteRating = () => {
  videoCallStore.cleanRaiting();
};

const handleCloseRating = () => {
  videoCallStore.cleanRaiting();
};

onBeforeMount(async () => {
  if (process.env.IONIC) {
    const Module = await import("@/Views/preview/index.vue");

    PreviewViewComp.value = Module.default;
  }

  if (isApp()) {
    showPreview.value = appStore.isNeededPreview();
  } else {
    showPreview.value = false;
  }
});

onMounted(async () => {
  if (process.env.IONIC) {
    const Module = await import("@/Views/preview/index.vue");

    PreviewViewComp.value = Module.default;
  }

  if (isApp()) {
    SplashScreen.hide();
  }

  appStore.checkUpdate();

  window.addEventListener("online", updateStatusOnline);
  window.addEventListener("offline", updateStatusOffline);
  window.addEventListener("resize", updateViewport);

  if (!isApp()) {
    document.addEventListener("visibilitychange", refreshTokenOnVisibilityChange);
  }

  updateViewport();
  addTrackers();

  if (isApp()) {
    try {
      Network.addListener("networkStatusChange", (status) => {
        if (route.name === APP_ROUTES.onboarding.root.name) {
          log(`Onboarding change network is connected ${status.connected}`);
          return;
        } else if (status.connected && route.name === APP_ROUTES.error.offline.root.name) {
          router.push({ name: APP_ROUTES.chat.root.name });
        } else if (!status.connected && !hasVideoCallInProgress.value) {
          appStore.clearStores(false);

          router.push({ name: APP_ROUTES.error.offline.root.name });
        }
      });

      if (isApp()) {
        App.addListener("appStateChange", async ({ isActive }) => {
          if (isActive) {
            await refreshTokenIfNeeded();

            addTrackers();
            PushNotifications.removeAllDeliveredNotifications();
            appStore.checkUpdate();
          }
        });
      }

      App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
        log("appUrlOpen listener");

        if (isObject(event) && isValidString(event.url)) {
          log("Open with URL");

          // Example url: https://beerswift.app/tabs/tabs2
          // path = /tabs/tabs2
          const path = event.url.replace(new RegExp("https:[/]{2}[^/]+", "g"), "");
          const [pathname, queryString] = path.split("?");

          if (pathname === "/auth") {
            const query = new URLSearchParams(queryString);

            router.push({
              name: APP_ROUTES.sso.root.name,
              query: { sso_token: query.get("sso_token") },
            });
          } else {
            if (authStore.isUserLogged) {
              router.push({ name: APP_ROUTES.home.root.name });
            } else {
              router.push({ name: APP_ROUTES.login.root.name });
            }
          }
        }
      });

      const status = await Network.getStatus();

      if (!status.connected) {
        router.push({ name: APP_ROUTES.error.offline.root.name });
      }
    } catch (e) {
      danger("ERROR Network PLUGIN", e);
    }
  } else {
    addTrackersAndCookiesBanner();
  }
});

onUnmounted(() => {
  chatStore.closeWebSocket();

  window.removeEventListener("online", updateStatusOnline);
  window.removeEventListener("offline", updateStatusOffline);
  window.removeEventListener("resize", updateViewport);
  document.removeEventListener("visibilitychange", refreshTokenOnVisibilityChange);
  clearTimeout(tokenRefreshTimeout);

  if (isApp()) {
    App.removeAllListeners();

    try {
      Network.removeAllListeners();
    } catch (e) {
      danger(e);
    }
  }
});

if (!isApp()) {
  useHead(() => ({
    link: [{ rel: "canonical", href: route.meta.canonical as string }],
  }));
}
</script>

<template>
  <RouterWrapper>
    <EventCapacitorManager>
      <IonApp
        class="ionic_app"
        :class="{
          'ionic_app--ios': isIosApp(),
          'ionic_app--android': isAndroidApp(),
          'ionic_app--keyboard-open': appStore.isKeyboardOpen,
          'ionic_app--video_call': hasVideoCallInProgress && !videoCallStore.isHiddenVideoCall,
        }"
      >
        <div style="position: absolute; top: -1000px">
          <!-- svg gradient for loaders -->
          <svg width="0" height="0">
            <defs>
              <linearGradient
                id="cl1"
                gradientUnits="userSpaceOnUse"
                x1="0"
                y1="0"
                x2="1"
                y2="1"
                gradientTransform="translate(17.8333 19.0007) rotate(-26.565) scale(20.87 18.6766)"
              >
                <stop stop-color="#50ABED" />
                <stop offset="0.811989" stop-color="#50ABED" stop-opacity="0" />
                <stop offset="1" stop-color="#50ABED" stop-opacity="0" />
              </linearGradient>
            </defs>
          </svg>
          <!-- svg gradient for loaders -->
        </div>

        <Privacy v-if="!showPreview">
          <TcSnackbar
            :message="snackbarParams.message"
            :messageParameters="snackbarParams.messageParameters"
            :type="snackbarParams.type"
          />

          <VideoCallVonageWrapper
            v-if="hasVideoCallInProgress"
            :isHidden="videoCallStore.isHiddenVideoCall"
            @onShowChat="videoCallStore.toggleHideVideoCall"
          />

          <VideoCallRatingModal
            v-if="needRating"
            @onComplete="handleCompleteRating"
            @onClose="handleCloseRating"
          />
          <VideoCallConfig
            v-if="showVideoCallConfig"
            class="video_call_config"
            :class="{ ios_grid_app: isIosApp(), android_grid_app: isAndroidApp() }"
          />

          <TcModal
            v-if="
              appStore.needUpdate &&
              hasValue(appStore.versionRequirements) &&
              hasValue(appStore.versionRequirements.current)
            "
            :acceptButtonText="t('general.force_update.button')"
            :title="t('general.force_update.title', { version: appStore.versionRequirements.current })"
            :textContent="t('general.force_update.description')"
            @acceptButtonClicked="handleUpdateApp"
            @clickOutside="handleCloseUpdateApp"
          />

          <RouterView />
        </Privacy>

        <component
          :is="PreviewViewComp"
          class="preview_view"
          :class="{ ios_grid_app: isIosApp(), android_grid_app: isAndroidApp() }"
          v-else
          @onFinish="handleFinishPreview"
          @onLogin="handleGoToLogin"
        />
      </IonApp>
    </EventCapacitorManager>
  </RouterWrapper>
</template>

<style lang="scss">
:root {
  --app-height: 100%;
  --ion-safe-area-bottom: var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px));
  --ion-safe-area-top: var(--safe-area-inset-top, env(safe-area-inset-top, 0px));
}

div#app {
  font-size: 16px;
  overflow-y: hidden;
  width: 100%;
  height: 100vh;
}

.ionic_app {
  z-index: 2;
  height: 100vh;
  height: 100dvh;
  width: 100%;
  padding-bottom: var(--ion-safe-area-bottom);
  padding-top: var(--ion-safe-area-top);
  position: relative;
  display: block;

  &--keyboard-open {
    padding-bottom: 0;
  }

  &--video_call {
    padding-bottom: 0;
    padding-top: 0;
    height: 100vh !important;
  }
}

.preview_view {
  height: 100vh;
}

.video_call_config {
  position: absolute;
  height: 100vh;
  left: $size-navigation-width;
  top: 0;
  width: calc(100vw - $size-navigation-width);
  z-index: 10000000;
  overflow-y: scroll;

  @media (max-width: $breakpoint-m) {
    width: 100vw;
    left: 0;
  }
}
</style>
